import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { AppLayout } from '@/components/layouts/AppLayout';
import { resetConversationsVersion } from '@/modules/conversations/hooks/useConversationsVersion';

/**
 * The way out of a screen cannot live inside that screen.
 *
 * Shared between the plain `/conversations` route and the platform-admin tenant route: both mount
 * the same lazy V2 screen, and both need an escape hatch outside it if a render crash — or a lazy
 * chunk gone missing after a deploy — takes the toggle down with it. `classicPath` is the only thing
 * that differs between the two callers: where "back to classic" actually lands.
 */
export class ConversationsV2Boundary extends Component<
  { children: ReactNode; classicPath: string },
  { crashed: boolean }
> {
  state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error: unknown) {
    if (import.meta.env.DEV) console.error('[conversations] new screen crashed:', error);
  }

  render() {
    if (this.state.crashed) {
      return (
        <ConversationsV2Crashed
          classicPath={this.props.classicPath}
          onRecover={() => this.setState({ crashed: false })}
        />
      );
    }
    return this.props.children;
  }
}

function ConversationsV2Crashed({ classicPath, onRecover }: { classicPath: string; onRecover: () => void }) {
  const { t } = useTranslation('conversations');

  const backToClassic = () => {
    // Clear first: without this the reload the operator will inevitably try lands here again.
    resetConversationsVersion();
    onRecover();
    // A pinned ?v=2 outranks the stored value, so drop it too.
    window.location.assign(classicPath);
  };

  return (
    <AppLayout fullWidth>
      <div className="flex flex-col items-center justify-center h-screen gap-4 px-6 text-center">
        <AlertTriangle className="h-10 w-10 text-amber-500" />
        <p className="text-sm text-muted-foreground max-w-sm">
          {t(
            'versionToggle.crashed',
            'The new conversations screen failed to load. Your conversations are untouched — the classic screen still works.',
          )}
        </p>
        <button
          type="button"
          onClick={backToClassic}
          className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:opacity-90"
        >
          {t('versionToggle.backToClassic', 'Back to Classic')}
        </button>
      </div>
    </AppLayout>
  );
}
